import React, { createContext, useReducer, useContext } from "react";
import { QUESTIONS_RECEIVED, SET_QUESTION } from "../types/questions";
import {
  ATTEMPTS_RECEIVED,
  SET_SINGLE_ATTEMPT,
  SET_UPDATE,
} from "../types/attemps";
import { ANSWERS_RECEIVED, SET_ANSWER } from "../types/answers";
import AdjuntosService from "../services/AdjuntosService";
import AttemptsReducer from "../reducers/AttempsReducer";
import AttemptsService from "../services/AttemptsService";
import { SINGLE_TEST_RECEIVED } from "../types/tests";
import { ModalContext } from "./ModalContext";
import { navigate } from "@reach/router";
import { S3_ENDPOINT } from "../utils";
import { HIDE_SPINNER, SHOW_SPINNER, SET_UPLOAD_PROGRESS } from "../types";

const initialState = {
  update: false,
  attempt: null,
  spinner: false,
  attempts: null,
  question: null,
  questions: null,
  uploadProgress: null,
};

export const AttempsContext = createContext(initialState);

export const AttemptsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(AttemptsReducer, initialState);

  const { clearModal, success } = useContext(ModalContext);

  const getUserAttempts = () => {
    AttemptsService.getUserAttempts().then((res) => {
      const { attempts } = res.data;
      dispatch({ type: ATTEMPTS_RECEIVED, payload: attempts });
    });
  };

  const getUserTestAttempts = (test_id) => {
    AttemptsService.getTestAttempts(test_id).then((res) => {
      const { attempts } = res.data;
      dispatch({ type: ATTEMPTS_RECEIVED, payload: attempts });
    });
  };

  const getSingleAttempt = (attempt_id) => {
    AttemptsService.getSingleAttempt(attempt_id).then((res) => {
      const { attempt } = res.data;
      const { test, user_answers } = attempt;
      const { questions } = test;
      dispatch({ type: QUESTIONS_RECEIVED, payload: questions });
      dispatch({ type: SINGLE_TEST_RECEIVED, payload: test });
      dispatch({ type: ANSWERS_RECEIVED, payload: user_answers });
      dispatch({ type: SET_SINGLE_ATTEMPT, payload: attempt });
    });
  };

  const createAttempt = (test_id, showSuccess = true) => {
    AttemptsService.postAttempt(test_id).then((res) => {
      const { attempt } = res.data;
      if (showSuccess) {
        success("Intento iniciado.");
        navigate(`/mytbm/attempt/${attempt.attempt_id}`);
      }
      clearModal();
    });
  };

  const finishAttempt = (attempt_id, user_answers) => {
    const answersToUpload = user_answers.filter(
      ({ user_answer_id }) => !user_answer_id
    );
    const promises = [];
    answersToUpload.forEach((currentAnswer) => {
      promises.push(
        AttemptsService.postUserAnswer({ ...currentAnswer, attempt_id })
      );
    });
    Promise.all(promises).then(() => {
      AttemptsService.finishAttempt(attempt_id).then(() => {
        getSingleAttempt(attempt_id);
        success("Intento terminado");
        clearModal();
      });
    });
  };

  const saveUserAnswer = (user_answer, callback) => {
    dispatch({ type: SHOW_SPINNER });

    // The caller decides whether saving should refetch (see QuizQuestion:
    // only final exams do), so this must not force an update of its own.
    const handleSuccess = () => {
      success("Respuesta guardada.");
      dispatch({ type: HIDE_SPINNER });
      if (typeof callback === "function") {
        callback();
      }
    };

    const handleError = (error) => {
      alert(error);
      dispatch({ type: HIDE_SPINNER });
    };

    let service = AttemptsService.putUserAnswer;
    if (isNaN(parseInt(user_answer.user_answer_id))) {
      service = AttemptsService.postUserAnswer;
    }

    if (typeof user_answer.content === "object") {
      // Large videos upload directly to S3 in parallel parts instead of being
      // proxied through the API, so a ~650 MB file no longer takes minutes.
      // Scoped to the question so only its own answer renders the progress bar.
      const setProgress = (progress) =>
        dispatch({
          type: SET_UPLOAD_PROGRESS,
          payload: { question_id: user_answer.question_id, progress },
        });
      setProgress(0);
      AdjuntosService.uploadFileMultipart(user_answer.content, setProgress)
        .then((res) => {
          const { file } = res;
          user_answer.content = `${S3_ENDPOINT}/${file.name}.${file.type}`;
          service(user_answer).then(handleSuccess).catch(handleError);
        })
        .catch(handleError);
    } else {
      service(user_answer).then(handleSuccess).catch(handleError);
    }
  };

  const setQuestion = (question) => {
    dispatch({ type: SET_QUESTION, payload: question });
  };

  const setAnswer = (question_id, content) => {
    dispatch({ type: SET_ANSWER, payload: { question_id, content } });
  };

  const setUpdate = (update) => {
    dispatch({ type: SET_UPDATE, payload: update });
  };

  const clearAttempt = () => {
    dispatch({ type: SET_QUESTION, payload: null });
    dispatch({ type: ANSWERS_RECEIVED, payload: null });
    dispatch({ type: QUESTIONS_RECEIVED, payload: null });
    dispatch({ type: SET_SINGLE_ATTEMPT, payload: null });
    dispatch({ type: SINGLE_TEST_RECEIVED, payload: null });
  };

  const saveAttempt = (attempt, message) => {
    AttemptsService.putAttempt(attempt).then(() => {
      getSingleAttempt(attempt.attempt_id);
      success(message && message !== null ? message : "Intento guardado.");
    });
  };

  return (
    <AttempsContext.Provider
      value={{
        ...state,
        getUserTestAttempts,
        getUserAttempts,
        getSingleAttempt,
        saveUserAnswer,
        finishAttempt,
        createAttempt,
        clearAttempt,
        setQuestion,
        saveAttempt,
        setAnswer,
        setUpdate,
      }}
    >
      {children}
    </AttempsContext.Provider>
  );
};
